<template>
    <section>
        <p class="mb-0 text-primary text-uppercase font-weight-bold">Select target</p>
        <div class="p-1">
            <select
                class="form-control form-control-sm"
                v-model="selectedTarget"
                title="selected-target"
                name="selected-target"
                id="selected-target"
            >
                <option v-if="!availableTargets.length" value="" disabled hidden :value="null"
                    >No target due to be tracked today</option
                >
                <option v-for="t in availableTargets" :value="t">{{ t }}</option>
            </select>
        </div>
        <p class="mb-0 mt-2 text-primary text-uppercase font-weight-bold">
            {{
                goal.parameters.targets.length && selectedTarget
                    ? goal.parameters.targets.find(target => selectedTarget === target.name).stage
                    : "Phase"
            }}
        </p>
        <div class="row px-3">
            <div class="col p-1">
                <prompt :iconset="6" :session="true" @select-prompt="addAttempt($event)"></prompt>
            </div>
        </div>
        <hr class="bar my-2" />
        <div class="row px-3">
            <div class="col p-1">
                <ul class="attempt-list pl-1">
                    <li class="pl-1" v-for="(a, i) in sessionTargets">
                        <span class="attempt-icon ml-1 mb-2 d-flex justify-content-start">
                            <span class="mr-2 small text-nowrap">{{ i }}:</span>
                            <span class="d-flex justify-content-start">
                                <prompt
                                    v-for="(b, j) in a.result"
                                    :key="`${i}-${j}`"
                                    :mini="true"
                                    :prompt="[null, 6, b]"
                                    :wrapper-class="'mx-1'"
                                ></prompt>
                            </span>
                        </span>
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<script>
import Prompt from "../../ui/PromptIcon";

export default {
    name: "ColdProbe",
    props: ["goal", "date", "notesData"],
    components: { Prompt },
    data() {
        return {
            selectedTarget: null,
            availableTargets: [], // Populates the select input
            sessionTargets: {}, // Used for presentation and creating session tracker data
            updatedGoalTargets: [], // Used to update the goal
            undoStack: JSON.stringify([]),
            totalRun: 0,
            totalSuccess: 0,
            masteredCount: 0,
        };
    },

    mounted() {
        this.initialSetup();
    },

    methods: {
        initialSetup() {
            if (this.goal) {
                this.goal.parameters.targets
                    .filter(
                        target =>
                            !target.mastered &&
                            (!target.next_probe ||
                                parseInt(this.moment(target.next_probe).format("YYYYDDD")) <=
                                    parseInt(this.moment(this.date).format("YYYYDDD")))
                        // Goals available: are not mastered and target.next_probe is before $props.date
                    )
                    .forEach(availableTarget => {
                        this.availableTargets.push(availableTarget.name);
                        this.$set(this.sessionTargets, availableTarget.name, {
                            stage: availableTarget.stage,
                            result: [],
                        });
                    });
                this.selectedTarget = this.availableTargets.length
                    ? this.availableTargets[0]
                    : null;
            }
        },

        undoAttempt() {
            let undoStack = JSON.parse(this.undoStack);
            if (undoStack.length) {
                const undoData = undoStack.pop();
                this.totalRun = undoData.totalRun;
                this.masteredCount = undoData.masteredCount;
                this.totalSuccess = undoData.totalSuccess;
                this.selectedTarget = JSON.parse(undoData.selectedTarget);
                this.sessionTargets = JSON.parse(undoData.sessionTargets);
                this.updatedGoalTargets = JSON.parse(undoData.updatedGoalTargets);
                this.availableTargets = JSON.parse(undoData.availableTargets);
                this.undoStack = JSON.stringify(undoStack);
            }
        },

        addAttempt(event) {
            if (!this.selectedTarget) return;
            this.populateUndoStack();
            this.updateTrackingState(event);
            this.updateGoalData(event);
            this.shouldAdvanceToNextTarget();
        },

        populateUndoStack() {
            let undoData = {};
            undoData.totalRun = this.totalRun;
            undoData.masteredCount = this.masteredCount;
            undoData.totalSuccess = this.totalSuccess;
            undoData.selectedTarget = JSON.stringify(this.selectedTarget);
            undoData.sessionTargets = JSON.stringify(this.sessionTargets);
            undoData.updatedGoalTargets = JSON.stringify(this.updatedGoalTargets);
            undoData.availableTargets = JSON.stringify(this.availableTargets);
            let prevStack = JSON.parse(this.undoStack);
            prevStack.push(undoData);
            this.undoStack = JSON.stringify(prevStack);
        },

        updateTrackingState(event) {
            this.sessionTargets[this.selectedTarget].result.push(event[0]);
            this.totalRun++;
            this.totalSuccess += event[0];
        },

        updateGoalData(event) {
            const targetUpdated = this.updatedGoalTargets.find(t => t.name === this.selectedTarget);
            const targetBase = this.goal.parameters.targets.find(
                t => t.name === this.selectedTarget
            );
            const target = targetUpdated
                ? JSON.parse(JSON.stringify(targetUpdated))
                : JSON.parse(JSON.stringify(targetBase));
            const data = { target, result: event[0] };
            data.target.updated = this.moment(this.date).format("YYYY-MM-DD");
            switch (data.target.stage) {
                case "baseline":
                    return this.treatBaseline(data);
                case "teaching":
                    return this.treatTeaching(data);
                case "acquired":
                    return this.treatAcquired(data);
            }
        },

        treatBaseline(data) {
            let targetCompleted = false;
            if (data.target.baseline.length === 0) {
                data.target.introduced = this.moment(this.date).format("YYYY-MM-DD");
            }
            if (
                data.target.baseline.length <
                this.goal.parameters.baseline_num_times_repeat_question
            ) {
                data.target.baseline.push(data.result);
            }
            if (
                data.target.baseline.length >=
                this.goal.parameters.baseline_num_times_repeat_question
            ) {
                if (
                    data.target.baseline.filter(b => b).length >=
                    this.goal.parameters.baseline_considered_mastered
                ) {
                    data.target.stage = "mastered";
                    this.masteredCount++;
                    data.target.retained = this.moment(this.date).format("YYYY-MM-DD");
                    data.target.mastered = true;
                } else {
                    data.target.stage = "teaching";
                    if (this.goal.parameters.teaching_is_consecutive_days_required) {
                        data.target.next_probe = this.moment(this.date)
                            .add(1, "d")
                            .format("YYYY-MM-DD");
                    }
                }
                targetCompleted = true;
            }
            let i = this.updatedGoalTargets.findIndex(
                updatedTarget => updatedTarget.name === data.target.name
            );
            if (i !== -1) {
                this.$set(this.updatedGoalTargets, i, data.target);
            } else {
                this.updatedGoalTargets.push(data.target);
            }
            return targetCompleted;
        },

        treatTeaching(data) {
            data.target.teaching.push(data.result);
            let failCount = 0;
            if (
                data.target.teaching.length >=
                this.goal.parameters.teaching_num_times_repeat_question
            ) {
                for (let i = 1; i <= this.goal.parameters.teaching_num_times_repeat_question; i++) {
                    if (!data.target.teaching[data.target.teaching.length - i]) {
                        failCount++;
                    }
                }
            }
            if (
                !failCount &&
                data.target.teaching.length >=
                    this.goal.parameters.teaching_num_times_repeat_question
            ) {
                data.target.acquired = this.moment(this.date).format("YYYY-MM-DD");
                data.target.stage = "acquired";
                data.target.next_probe = this.moment(this.date)
                    .add(this.goal.parameters.retention_num_days_reprobe, "d")
                    .format("YYYY-MM-DD");
            } else {
                if (this.goal.parameters.teaching_is_consecutive_days_required) {
                    data.target.next_probe = this.moment(this.date)
                        .add(1, "d")
                        .format("YYYY-MM-DD");
                }
            }
            this.updatedGoalTargets.push(data.target);
            return true;
        },

        treatAcquired(data) {
            if (data.result) {
                data.target.stage = "mastered";
                this.masteredCount++;
                data.target.mastered = "true";
                data.target.retained = this.moment(this.date).format("YYYY-MM-DD");
            } else {
                data.target.stage = "teaching";
                data.target.teaching = [];
                if (this.goal.parameters.teaching_is_consecutive_days_required) {
                    data.target.next_probe = this.moment(this.date)
                        .add(1, "d")
                        .format("YYYY-MM-DD");
                }
            }
            this.updatedGoalTargets.push(data.target);
            return true;
        },

        shouldAdvanceToNextTarget() {
            const targetBeforeUpdate = this.goal.parameters.targets.find(
                target => target.name === this.selectedTarget
            );
            if (targetBeforeUpdate.stage !== "baseline") {
                this.advanceToNextTarget();
            } else {
                const updatedGoalTarget = this.updatedGoalTargets.find(
                    t => t.name === this.selectedTarget
                );
                if (
                    updatedGoalTarget.baseline.length >=
                    this.goal.parameters.baseline_num_times_repeat_question
                ) {
                    this.advanceToNextTarget();
                }
            }
        },

        advanceToNextTarget() {
            const i = this.availableTargets.indexOf(this.selectedTarget);
            if (i >= 0) {
                this.availableTargets.splice(i, 1); // Removes tested target from available pool
            }
            this.selectedTarget = this.availableTargets.length ? this.availableTargets[0] : null;
        },

        saveTrackerData() {
            let data = {
                session_at: this.date
                    ? this.moment(this.date).format("YYYY-MM-DD HH:mm:ss")
                    : this.moment().format("YYYY-MM-DD HH:mm:ss"),
                data: {
                    targets: [],
                },
            };

            Object.keys(this.sessionTargets).forEach(k => {
                data.data.targets.push({
                    name: k,
                    result: this.sessionTargets[k].result,
                    stage: this.sessionTargets[k].stage,
                });
            });

            let parameters = JSON.parse(JSON.stringify(this.goal.parameters));
            parameters.targets.forEach((parameterTarget, pIndex) => {
                this.updatedGoalTargets.forEach(updatedTarget => {
                    if (parameterTarget.name === updatedTarget.name) {
                        parameters.targets[pIndex] = updatedTarget;
                    }
                });
            });
            let parsedParams = JSON.parse(JSON.stringify(parameters));
            axios
                .post(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`, data)
                .then(r => {
                    this.saveGoalData({ parameters: parsedParams });
                })
                .catch(error => {
                    this.$emit("save-draft", { error, data });

                    const method = "PATCH";
                    const url = `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`;
                    this.$emit("save-draft", {
                        error,
                        data: { parameters: parsedParams },
                        endpoint: { method, url },
                    });

                    this.reportResult(false);
                    console.error(error);
                });
        },

        saveGoalData(updatedData) {
            //Updates Goal's targets' copy with the updated ones
            const method = "PATCH";
            const url = `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`;
            axios({
                method,
                url,
                data: updatedData,
            })
                .then(response => {
                    this.reportResult(true);
                })
                .catch(error => {
                    this.$emit("save-draft", { error, data, endpoint: { method, url } });
                    this.$swal({
                        title: "Error!",
                        text: "An error occurred while saving.",
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                    console.error(error);
                    this.reportResult(false);
                });
        },

        reportResult(success) {
            this.$emit("saved", success);
            if (success) {
                this.$emit(
                    "close-tracker",
                    this.goal.title +
                        `: ${this.updatedGoalTargets.length} probes tracked today, ${
                            this.masteredCount
                        } mastered - ` +
                        this.notesData
                );
            }
        },
    },
};
</script>

<style scoped lang="scss">
.attempt-list {
    list-style: none;
}
</style>
